import { useCallback, useState } from 'react';
import { Tabs } from './components/Tabs';
import { ToastProvider } from './components/Toast';
import { LanguageProvider } from './lib/language';
import { PreferencesProvider } from './lib/preferences';
import { LanguageToggle } from './components/LanguageToggle';
import { CatalogueRoute } from './routes/Catalogue';
import { GalleryRoute } from './routes/Gallery';
import { StudioRoute } from './routes/Studio';
import { useTheme } from './lib/theme';
import { IconBookmark } from './components/Icon';

type TabId = 'studio' | 'catalogue' | 'gallery';

const TABS: { id: TabId; label: string }[] = [
  { id: 'studio', label: 'Studio' },
  { id: 'catalogue', label: 'Catalogue' },
  { id: 'gallery', label: 'Gallery' },
];

/**
 * v6-polish — split out the inner AppShell so we can call
 * `useTheme()` (which now reads from PreferencesContext) only AFTER
 * PreferencesProvider is mounted. Putting the hook call here means we
 * keep the original App() export while satisfying the rule "context
 * hooks must be called inside their provider".
 */
function AppShell() {
  const [tab, setTab] = useState<TabId>('studio');
  const theme = useTheme();

  // v5 a11y — global polite status announcer.
  // Screen-reader-only `<div aria-live="polite">` mounted at app root.
  // Components call `announce(msg)` to push a transient status update
  // (e.g. "Four variations are ready — pick one") without re-routing
  // focus or grabbing attention more than necessary.
  const [announcement, setAnnouncement] = useState('');
  const announce = useCallback((msg: string) => {
    // Two-step write ensures screen readers detect the change even when
    // the same message is announced back-to-back.
    setAnnouncement('');
    window.setTimeout(() => setAnnouncement(msg), 50);
  }, []);

  return (
    <LanguageProvider>
      <ToastProvider>
        {/*
          v5 a11y — skip-link. Visible only when focused; jumps the keyboard
          user past the header chrome straight into <main>. Critical for
          screen-reader / keyboard-only users on a route with a sticky header.
        */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-accent focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
        >
          Skip to main content
        </a>

        <header className="sticky top-0 z-30 flex flex-col">
          {/*
            v6-polish — <Tabs> is the FIRST child of <header> so it lands
            first in the keyboard tab order. `order-2` then visually
            drops it below the title row (which has the default flex
            order of 0). Tab order ends up:
              tabs → lang/theme → image toolbar → ... → chat input.
          */}
          <Tabs
            tabs={TABS}
            active={tab}
            onChange={(id) => setTab(id as TabId)}
            className="order-2"
          />
          <div className="flex items-center justify-between gap-3 border-b border-neutral-200 bg-white/80 px-4 py-3 backdrop-blur dark:border-neutral-800 dark:bg-neutral-950/80">
            <div className="flex items-center gap-3">
              <span aria-hidden="true" className="text-accent">
                <IconBookmark size={22} aria-hidden />
              </span>
              <h1 className="text-base font-semibold tracking-tight">Stamps</h1>
            </div>
            <div className="flex items-center gap-2">
              {/* v6 — plain / technical language toggle. */}
              <LanguageToggle />
              <button
                type="button"
                onClick={theme.toggle}
                aria-label={`Switch to ${theme.theme === 'dark' ? 'light' : 'dark'} theme`}
                className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-md border border-neutral-300 px-3 text-sm font-medium text-neutral-800 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:text-neutral-100 dark:hover:bg-neutral-800"
              >
                {theme.theme === 'dark' ? (
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4"
                  >
                    <circle cx="12" cy="12" r="4" />
                    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                  </svg>
                ) : (
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4"
                  >
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
                  </svg>
                )}
                <span>{theme.theme === 'dark' ? 'Dark' : 'Light'}</span>
              </button>
            </div>
          </div>
        </header>

        <main
          id="main-content"
          tabIndex={-1}
          className="mx-auto max-w-7xl px-4 py-6 focus:outline-none sm:px-6 lg:px-8"
        >
          {/* Render only the active route so its useEffect / fetch logic only
              fires when the user actually opens that tab. Previously all three
              routes were mounted simultaneously via `hidden`, which caused the
              Catalogue/Gallery polling loop to fire from page-load even when
              mum was on Studio. */}
          {tab === 'studio' && <StudioRoute announce={announce} />}
          {tab === 'catalogue' && <CatalogueRoute />}
          {tab === 'gallery' && <GalleryRoute />}
        </main>

        {/*
          v5 a11y — polite live region for status updates (variations ready,
          save succeeded, etc). Mounted once at app root so any component
          can push a message via the `announce` callback passed down through
          context-style props.
        */}
        <div
          aria-live="polite"
          aria-atomic="true"
          role="status"
          className="sr-only"
        >
          {announcement}
        </div>
      </ToastProvider>
    </LanguageProvider>
  );
}

export function App() {
  // v6-polish — PreferencesProvider is the single source of truth for
  // lang / theme / loupeEnabled. LanguageProvider is kept as a no-op
  // wrapper inside AppShell for backwards compatibility with code that
  // imports it.
  return (
    <PreferencesProvider>
      <AppShell />
    </PreferencesProvider>
  );
}